/**
 * Geometric utility functions for mind map positioning and calculations
 */

/**
 * Calculate a point at a specific angle and distance from origin
 * @param {number} x - Origin X coordinate
 * @param {number} y - Origin Y coordinate
 * @param {number} degrees - Angle in degrees (0 = right, 90 = down, 180 = left, 270 = up)
 * @param {number} distance - Distance from origin
 * @returns {{x: number, y: number}} New point coordinates
 */
export function getPointAtAngle(x, y, degrees, distance) {
  const radians = (degrees * Math.PI) / 180;

  return {
    x: x + distance * Math.cos(radians),
    y: y + distance * Math.sin(radians)
  };
}

/**
 * Calculate angle and distance between two points
 * @param {number} x1 - First point X coordinate
 * @param {number} y1 - First point Y coordinate
 * @param {number} x2 - Second point X coordinate
 * @param {number} y2 - Second point Y coordinate
 * @returns {{angle: number, distance: number}} Angle in degrees and distance
 */
export function getAngleAndDistance(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;

  const distance = Math.sqrt(dx * dx + dy * dy);
  const angleRadians = Math.atan2(dy, dx);
  const angleDegrees = (angleRadians * 180) / Math.PI;

  return {
    distance,
    angle: angleDegrees
  };
}

/**
 * Normalize angle to 0-360 range
 * @param {number} angle - Angle in degrees
 * @returns {number} Normalized angle (0-360)
 */
export function normalizeAngle(angle) {
  return ((angle % 360) + 360) % 360;
}

/**
 * Calculate the shortest angular distance between two angles
 * @param {number} angle1 - First angle in degrees
 * @param {number} angle2 - Second angle in degrees
 * @returns {number} Shortest angular distance (0-180)
 */
export function getAngularDistance(angle1, angle2) {
  const norm1 = normalizeAngle(angle1);
  const norm2 = normalizeAngle(angle2);

  return Math.min(
    Math.abs(norm1 - norm2),
    360 - Math.abs(norm1 - norm2)
  );
}

/**
 * Snap angle to nearest cardinal direction (0, 90, 180, 270)
 * @param {number} angle - Input angle in degrees
 * @returns {number} Snapped angle (0, 90, 180, or 270)
 */
export function snapToCardinal(angle) {
  const normalized = normalizeAngle(angle);

  if (normalized >= 315 || normalized < 45) {
    return 0;     // Right
  } else if (normalized >= 45 && normalized < 135) {
    return 90;    // Down
  } else if (normalized >= 135 && normalized < 225) {
    return 180;   // Left
  } else {
    return 270;   // Up
  }
}

/**
 * Snap angle to nearest increment
 * @param {number} angle - Input angle in degrees
 * @param {number} increment - Snap increment in degrees (default: 15)
 * @returns {number} Snapped angle
 */
export function snapToIncrement(angle, increment = 15) {
  const normalized = normalizeAngle(angle);
  return Math.round(normalized / increment) * increment;
}

/**
 * Calculate distance between two points
 * @param {number} x1 - First point X coordinate
 * @param {number} y1 - First point Y coordinate
 * @param {number} x2 - Second point X coordinate
 * @param {number} y2 - Second point Y coordinate
 * @returns {number} Distance between points
 */
export function getDistance(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Check if a point is within a rectangular area
 * @param {number} pointX - Point X coordinate
 * @param {number} pointY - Point Y coordinate
 * @param {number} rectX - Rectangle top-left X coordinate
 * @param {number} rectY - Rectangle top-left Y coordinate
 * @param {number} rectWidth - Rectangle width
 * @param {number} rectHeight - Rectangle height
 * @returns {boolean} True if point is within rectangle
 */
export function isPointInRect(pointX, pointY, rectX, rectY, rectWidth, rectHeight) {
  return pointX >= rectX &&
         pointX <= rectX + rectWidth &&
         pointY >= rectY &&
         pointY <= rectY + rectHeight;
}

/**
 * Check if a point is within a circular area
 * @param {number} pointX - Point X coordinate
 * @param {number} pointY - Point Y coordinate
 * @param {number} centerX - Circle center X coordinate
 * @param {number} centerY - Circle center Y coordinate
 * @param {number} radius - Circle radius
 * @returns {boolean} True if point is within circle
 */
export function isPointInCircle(pointX, pointY, centerX, centerY, radius) {
  const distance = getDistance(pointX, pointY, centerX, centerY);
  return distance <= radius;
}

/**
 * Calculate the center point between multiple points
 * @param {Array<{x: number, y: number}>} points - Array of points
 * @returns {{x: number, y: number}} Center point
 */
export function getCenterPoint(points) {
  if (points.length === 0) return { x: 0, y: 0 };

  const sum = points.reduce((acc, point) => ({
    x: acc.x + point.x,
    y: acc.y + point.y
  }), { x: 0, y: 0 });

  return {
    x: sum.x / points.length,
    y: sum.y / points.length
  };
}

/**
 * Calculate bounding box for a set of points
 * @param {Array<{x: number, y: number}>} points - Array of points
 * @returns {{x: number, y: number, width: number, height: number}} Bounding box
 */
export function getBoundingBox(points) {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * Linear interpolation between two values
 * @param {number} start - Start value
 * @param {number} end - End value
 * @param {number} t - Interpolation factor (0-1)
 * @returns {number} Interpolated value
 */
export function lerp(start, end, t) {
  return start + (end - start) * t;
}

/**
 * Linear interpolation between two points
 * @param {{x: number, y: number}} start - Start point
 * @param {{x: number, y: number}} end - End point
 * @param {number} t - Interpolation factor (0-1)
 * @returns {{x: number, y: number}} Interpolated point
 */
export function lerpPoint(start, end, t) {
  return {
    x: lerp(start.x, end.x, t),
    y: lerp(start.y, end.y, t)
  };
}

/**
 * Rotate a point around an origin
 * @param {number} pointX - Point X coordinate
 * @param {number} pointY - Point Y coordinate
 * @param {number} originX - Origin X coordinate
 * @param {number} originY - Origin Y coordinate
 * @param {number} degrees - Rotation angle in degrees
 * @returns {{x: number, y: number}} Rotated point
 */
export function rotatePoint(pointX, pointY, originX, originY, degrees) {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const translatedX = pointX - originX;
  const translatedY = pointY - originY;

  return {
    x: translatedX * cos - translatedY * sin + originX,
    y: translatedX * sin + translatedY * cos + originY
  };
}

/**
 * Calculate the intersection point of two lines
 * @param {number} x1 - Line 1 point 1 X
 * @param {number} y1 - Line 1 point 1 Y
 * @param {number} x2 - Line 1 point 2 X
 * @param {number} y2 - Line 1 point 2 Y
 * @param {number} x3 - Line 2 point 1 X
 * @param {number} y3 - Line 2 point 1 Y
 * @param {number} x4 - Line 2 point 2 X
 * @param {number} y4 - Line 2 point 2 Y
 * @returns {{x: number, y: number}|null} Intersection point or null if parallel
 */
export function getLineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (Math.abs(denom) < 1e-10) {
    return null; // Lines are parallel
  }

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;

  return {
    x: x1 + t * (x2 - x1),
    y: y1 + t * (y2 - y1)
  };
}